import { criarRepositorio, mesmoEstado } from '@/utils/listaPredefinicoes';

/**
 * Maquinário comum às duas abas da tela de cobranças: período, busca, filtros, colunas,
 * predefinições, paginação, ordenação e totalizadores.
 *
 * A aba que usa este mixin fornece, no seu próprio `data()`:
 *   endpoint             — rota da listagem (a de totais é `${endpoint}/totais`, salvo `endpointTotais`)
 *   catalogoColunas      — criarCatalogoColunas(...)
 *   catalogoFiltros      — criarCatalogoFiltros(...)
 *   chavePredefinicoes   — chave no localStorage
 *   permissaoConsultar   — resource exigido para listar
 *   permissaoTotais      — resource exigido para ver os totalizadores
 *   colunas / filtros    — estado inicial vindo dos catálogos
 */

/**
 * Períodos relativos ("Últimos 7 dias") recalculam as datas a cada abertura, então comparar
 * start/end acusaria "alterações não salvas" só porque o dia virou. O que identifica o período é o
 * rótulo — exceto no Personalizado, onde as datas são a própria escolha.
 */
export function chavePeriodo(periodo) {
    if (!periodo || !periodo.periodo) {
        return '';
    }

    return periodo.periodo === 'Personalizado'
        ? `Personalizado|${periodo.start}|${periodo.end}`
        : periodo.periodo;
}

export default {
    data: () => ({
        search: '',
        loading: false,
        loadingTotais: false,
        dados: [],
        totais: null,
        periodo: {},
        periodoKey: 0,
        predefinicoes: [],
        ativaId: null,
        options: { sortBy: [], sortDesc: [] },
        ultimaOrdenacao: JSON.stringify([[], []]),
        pagination: {
            current_page: 1,
            last_page: 1,
            total: 0,
        },
    }),

    computed: {
        podeConsultar() {
            return this.checkPermission(this.permissaoConsultar);
        },

        podeVerTotais() {
            return this.checkPermission(this.permissaoTotais);
        },

        chips() {
            return this.catalogoFiltros.chipsAtivos(this.filtros);
        },

        estadoAtual() {
            return {
                colunas: this.colunas,
                filtros: this.filtros,
                periodo: this.periodo,
            };
        },

        predefinicaoAtiva() {
            return this.predefinicoes.find(p => p.id === this.ativaId) || null;
        },

        sujo() {
            const ativa = this.predefinicaoAtiva;

            if (!ativa) {
                return false;
            }

            return !mesmoEstado(
                {
                    colunas: this.colunas,
                    filtros: this.filtros,
                    periodo: chavePeriodo(this.periodo),
                },
                {
                    colunas: this.catalogoColunas.normalizarColunas(ativa.colunas),
                    filtros: this.catalogoFiltros.normalizarFiltros(ativa.filtros),
                    periodo: chavePeriodo(ativa.periodo),
                },
            );
        },
    },

    watch: {
        options: {
            deep: true,
            handler(novo) {
                const chave = JSON.stringify([novo.sortBy, novo.sortDesc]);

                if (chave === this.ultimaOrdenacao) {
                    return;
                }

                this.ultimaOrdenacao = chave;
                this.aplicar();
            },
        },

        colunas() {
            this.persistirEstado();
        },
    },

    created() {
        this.repo = criarRepositorio(this.chavePredefinicoes);

        this.predefinicoes = this.repo.listar();
        this.ativaId = this.repo.ativaId();

        const estado = this.repo.lerEstado();

        if (estado) {
            this.aplicarEstado(estado);
        }
    },

    mounted() {
        this.consultar();
    },

    methods: {
        getParams() {
            const params = new URLSearchParams();

            // Período só entra quando já foi resolvido: sem a guarda, a primeira carga de um
            // navegador limpo mandaria "undefined" e o banco recusaria a data.
            if (this.periodo && this.periodo.start && this.periodo.end) {
                params.append('start', this.periodo.start);
                params.append('end', this.periodo.end);
            }

            if (this.search) {
                params.append('q', this.search);
            }

            this.catalogoFiltros.aplicarParams(params, this.filtros);

            if (this.options.sortBy.length) {
                params.append('sortField', this.options.sortBy[0]);
                params.append('sortOrder', this.options.sortDesc[0] ? 1 : -1);
            }

            return params;
        },

        consultar() {
            this.persistirEstado();

            if (!this.podeConsultar) {
                return;
            }

            const params = this.getParams();
            params.append('page', this.pagination.current_page);

            this.loading = true;
            this.dados = [];

            this.$http.get(`${this.endpoint}?${params.toString()}`)
                .then(resp => {
                    this.pagination = resp.data.meta;
                    this.dados = resp.data.data;
                })
                .catch(() => {
                    this.notify('Não foi possivel concluir a operação', 'warning');
                })
                .finally(() => (this.loading = false));

            this.consultarTotais();
        },

        consultarTotais() {
            if (!this.podeVerTotais) {
                return;
            }

            const rota = this.endpointTotais || `${this.endpoint}/totais`;

            this.loadingTotais = true;
            this.$http.get(`${rota}?${this.getParams().toString()}`)
                .then(resp => (this.totais = resp.data.data))
                .catch(() => (this.totais = null))
                .finally(() => (this.loadingTotais = false));
        },

        aplicar() {
            this.pagination.current_page = 1;
            this.consultar();
        },

        removerFiltro(chave) {
            const padrao = this.catalogoFiltros.filtrosPadrao();

            this.filtros = { ...this.filtros, [chave]: padrao[chave] };
            this.aplicar();
        },

        limparFiltros() {
            this.filtros = this.catalogoFiltros.filtrosPadrao();
            this.aplicar();
        },

        // --- predefinições ---

        persistirEstado() {
            this.repo.salvarEstado({
                colunas: this.colunas,
                filtros: this.filtros,
                periodo: this.periodo,
                search: this.search,
                page: this.pagination.current_page,
            });
        },

        aplicarEstado(estado) {
            this.colunas = this.catalogoColunas.normalizarColunas(estado.colunas);
            this.filtros = this.catalogoFiltros.normalizarFiltros(estado.filtros);

            if (estado.periodo && estado.periodo.periodo) {
                this.periodo = estado.periodo;
                // O PeriodoFilter só lê o v-model ao montar; remontar é o que faz ele recalcular
                // as datas do período restaurado sem mexer na API de um componente compartilhado.
                this.periodoKey++;
            }

            if (estado.search !== undefined) {
                this.search = estado.search;
            }

            if (estado.page) {
                this.pagination.current_page = estado.page;
            }
        },

        recarregarPredefinicoes() {
            this.predefinicoes = this.repo.listar();
            this.ativaId = this.repo.ativaId();
        },

        nomeEmUso(nome, ignorarId) {
            return this.repo.nomeEmUso(nome, ignorarId);
        },

        aplicarPredefinicao(id) {
            const item = this.repo.obter(id);

            if (!item) {
                return;
            }

            this.repo.definirAtiva(id);
            this.aplicarEstado(item);
            this.recarregarPredefinicoes();
            this.aplicar();
        },

        aplicarPadrao() {
            this.repo.definirAtiva(null);
            this.aplicarEstado({
                colunas: this.catalogoColunas.colunasPadrao(),
                filtros: this.catalogoFiltros.filtrosPadrao(),
            });
            this.recarregarPredefinicoes();
            this.aplicar();
        },

        criarPredefinicao(nome) {
            this.repo.criar(nome, this.estadoAtual);
            this.recarregarPredefinicoes();
            this.notify('Predefinição salva');
        },

        salvarPredefinicao() {
            if (!this.ativaId) {
                return;
            }

            this.repo.atualizar(this.ativaId, this.estadoAtual);
            this.recarregarPredefinicoes();
            this.notify('Predefinição atualizada');
        },

        renomearPredefinicao(nome) {
            this.repo.renomear(this.ativaId, nome);
            this.recarregarPredefinicoes();
        },

        duplicarPredefinicao(nome) {
            this.repo.duplicar(this.ativaId, nome);
            this.recarregarPredefinicoes();
            this.notify('Predefinição duplicada');
        },

        removerPredefinicao(id) {
            this.repo.remover(id);
            this.recarregarPredefinicoes();
            this.notify('Predefinição excluída');
        },

        // --- grid ---

        manterOrdemDoServidor(items) {
            return items;
        },
    },
};
